// webapp/src/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { consumeAuthAttempt, clearAuthAttempt } from './authActivity';
import GuestVerification from './GuestVerification';
import { appUrl } from './appNavigation';
import { completeGuestUpgrade, ensureGuestSession, guestRequest } from './guestSession';
import {
    setAnalyticsUser,
    resolveGoogleAnalyticsAttribution,
    trackEvent,
    trackDiagnostic,
    trackGuestUpgradeCompleted,
    trackGuestUpgradeAccountVerified,
    trackGuestUpgradeHistorySaved,
    trackMetaCompleteRegistration,
    trackRedditSignUp,
    trackSignupCompleted,
} from './analytics';
import { buildProfileAttribution, clearCompletedAuthContext, clearSignupAttempt, getAuthAttribution, getSavedSignupAttempt } from './authAttribution';
import { trackPromoteKitSignup } from './promotekit';
import { authErrorParams } from './authErrors';
import { clearSignupAttributionCache, getSignupAttribution, isRecentRegistration, loadSignupAttribution,
    originalAcquisition, registrationMethod } from './signupAttribution';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const isEmptyProfileValue = (value) => value === null || value === undefined || value === '';

const buildFirstTouchUpdate = (profile, attribution, user) => {
    const payload = {};

    if (user?.email && isEmptyProfileValue(profile?.email)) {
        payload.email = user.email;
    }

    Object.entries(attribution).forEach(([key, value]) => {
        if (isEmptyProfileValue(value)) return;
        if (isEmptyProfileValue(profile?.[key])) {
            payload[key] = value;
        }
    });

    return payload;
};

const persistSignedInAuthMetadata = async (user, attribution) => {
    const currentMetadata = user?.user_metadata || {};
    const payload = buildFirstTouchUpdate(currentMetadata, attribution);
    delete payload.email;
    // Consent changes are current state; immutable first-touch rules do not
    // apply to consent or the ID that must be removed on withdrawal.
    if (attribution.ga_tracking_status && attribution.ga_tracking_status !== currentMetadata.ga_tracking_status) {
        payload.ga_tracking_status = attribution.ga_tracking_status;
    }
    if (attribution.ga_tracking_status === 'consent_denied') {
        if (currentMetadata.ga_client_id != null) payload.ga_client_id = null;
    } else if (attribution.ga_client_id && attribution.ga_client_id !== currentMetadata.ga_client_id) {
        payload.ga_client_id = attribution.ga_client_id;
    }
    if (!Object.keys(payload).length) return;

    const { error } = await supabase.auth.updateUser({
        data: {
            ...currentMetadata,
            ...payload,
        },
    });
    if (error) console.warn('Auth attribution metadata warning:', error.message || error);
};

const persistSignedInProfileAttribution = async (user, method, authAttribution = {}, signupAttempt = {}) => {
    if (!user?.id) return;

    await loadSignupAttribution(user).catch(error => console.warn('Signup attribution warning:', error.message));
    await resolveGoogleAnalyticsAttribution();

    const snapshot = getSignupAttribution(user);
    const authTarget = snapshot?.guest_created_at && snapshot.registered_at ? 'signup'
        : authAttribution.auth_target || (signupAttempt.method ? 'signup' : undefined);
    const attribution = buildProfileAttribution({
        method: signupAttempt.method || method,
        authTarget,
        source: 'webapp',
        user,
    });

    await persistSignedInAuthMetadata(user, attribution);
    const { error } = await supabase.rpc('persist_profile_attribution', { attribution });
    if (error) {
        trackDiagnostic('profile_attribution_error', error, { operation: 'persist_profile_attribution' });
        console.warn('Profile attribution persistence failed:', error.code || 'request_failed');
    }

};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [guestConfig, setGuestConfig] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const [handoverError, setHandoverError] = useState('');
    const closeChallenge = useCallback(() => setChallenge(null), []);
    const ensureSession = useCallback(() => ensureGuestSession((siteKey) => new Promise((resolve, reject) => {
        setChallenge({ siteKey, resolve, reject });
    })).then((session) => { setCurrentUser(session.user); return session; }), []);
    useEffect(() => {
        let alive = true;
        guestRequest('config').then(c => { if (alive) setGuestConfig(c); }).catch(() => { if (alive) setGuestConfig({ enabled: false }); });
        return () => { alive = false; };
    }, []);
    const saveGuestHistory = useCallback(async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            trackGuestUpgradeAccountVerified(session?.user);
            const claimed = await completeGuestUpgrade(session);
            if (claimed?.saved) trackGuestUpgradeHistorySaved(session?.user);
            trackGuestUpgradeCompleted(session?.user);
            setHandoverError('');
        } catch (error) {
            trackEvent('guest_upgrade_error', { ...authErrorParams(error, 'history_handover') });
            setHandoverError(error.message);
        }
    }, []);
    useEffect(() => {
        if (currentUser && !currentUser.is_anonymous) saveGuestHistory();
    }, [currentUser, saveGuestHistory]);
    useEffect(() => {
        if (!currentUser?.is_anonymous) return;
        let lastTouch = 0;
        const touch = async () => {
            if (document.visibilityState !== 'visible' || Date.now() - lastTouch < 300000) return;
            lastTouch = Date.now();
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user.is_anonymous) guestRequest('activity', {}, session).catch(() => {});
        };
        window.addEventListener('pointerdown', touch);
        window.addEventListener('keydown', touch);
        return () => { window.removeEventListener('pointerdown', touch); window.removeEventListener('keydown', touch); };
    }, [currentUser?.is_anonymous]);
    const [isAuthInitialized, setIsAuthInitialized] = useState(false);
    const [hasReauthenticated, setHasReauthenticated] = useState(false);
    const [nextPage, setNextPage] = useState('');
    const [loading, setLoading] = useState(true);
    const [premiumState, setPremiumState] = useState(null);
    const isPremium = premiumState?.userId === currentUser?.id && premiumState?.premium === true;
    const [premiumLoading, setPremiumLoading] = useState(false);
    const [premiumError, setPremiumError] = useState(false);
    const [premiumCheckedUser, setPremiumCheckedUser] = useState(null);
    const premiumRequestRef = useRef(0);
    const premiumUserRef = useRef(currentUser?.id);
    premiumUserRef.current = currentUser?.id;
    const [referralEligibleUser, setReferralEligibleUser] = useState(null);
    const lastHealthUserId = useRef(null);

    const persistProfileAttributionForUser = useCallback((user, { completeAuthAttempt = false } = {}) => {
        if (!user?.id || user.is_anonymous) return null;

        const authAttribution = getAuthAttribution();
        const signupAttempt = getSavedSignupAttempt();
        const method = registrationMethod(user, signupAttempt);

        persistSignedInProfileAttribution(user, method, authAttribution, signupAttempt)
            .catch((error) => {
                trackDiagnostic('profile_attribution_error', error, { operation: 'persist_profile_attribution' });
                console.warn('Profile attribution persistence warning:', error?.message || error);
            }).finally(() => { if (completeAuthAttempt) clearCompletedAuthContext(); });

        return { authAttribution, signupAttempt, method };
    }, []);

    const fetchUser = useCallback(async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            setCurrentUser(session?.user || null);
            if (session?.user) {
                persistProfileAttributionForUser(session.user);
            }
            setIsAuthInitialized(true);
            setLoading(false);
        } catch (error) {
            console.error("Error getting session:", error);
            setLoading(false);
        }
    }, [persistProfileAttributionForUser]);

    useEffect(() => {
        // Set up Supabase auth listener
        const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            setCurrentUser(session?.user || null);
            setIsAuthInitialized(true);
            setLoading(false);
            if (['SIGNED_IN', 'INITIAL_SESSION', 'USER_UPDATED'].includes(event) && session?.user && !session.user.is_anonymous) {
                const attempt = consumeAuthAttempt(session.user);
                // Metadata writes also emit USER_UPDATED. Only an explicit
                // completed auth flow needs persistence on that event.
                const attributionContext = event !== 'USER_UPDATED' || attempt
                    ? persistProfileAttributionForUser(session.user, { completeAuthAttempt: Boolean(attempt) }) : null;
                const authAttribution = attributionContext?.authAttribution || getAuthAttribution();
                const method = attributionContext?.method || registrationMethod(session.user, getSavedSignupAttempt());
                setAnalyticsUser(session.user, { auth_provider: method });
                if (attempt) {
                    const params = { ...authAttribution, method: attempt.method, auth_target: attempt.target };
                    trackEvent('auth_success', params);
                    if (attempt.target === 'login') {
                        trackEvent('login', params);
                        if (authAttribution.from_landing) trackEvent('login_from_landing', params);
                    }
                }
            }
            if (event === 'SIGNED_OUT') {
                clearAuthAttempt();
                clearSignupAttributionCache();
                trackEvent('logout');
            }
        });

        // Initial session check
        fetchUser();

        // Cleanup subscription
        return () => {
            if (authListener && authListener.subscription) {
                authListener.subscription.unsubscribe();
            }
        };
    }, [fetchUser, persistProfileAttributionForUser]);

    useEffect(() => {
        if (!currentUser?.id) return;
        let alive = true;
        loadSignupAttribution(currentUser).then(snapshot => {
            if (!alive || currentUser.is_anonymous) return;
            const attempt = getSavedSignupAttempt();
            const attribution = getAuthAttribution();
            const method = registrationMethod(currentUser, attempt);
            setAnalyticsUser(currentUser, { auth_provider: method });
            // Guest identity linking can emit USER_UPDATED or INITIAL_SESSION,
            // and keeps the old created_at. Use the database registration time.
            if (isRecentRegistration(currentUser)
                && (snapshot.guest_created_at || attempt.method || attribution.auth_target === 'signup')) {
                const params = { ...attribution, ...originalAcquisition(currentUser), method,
                    source: snapshot.guest_created_at ? 'guest_upgrade' : attempt.source || 'auth_callback' };
                if (trackSignupCompleted(params, currentUser.id)) {
                    trackMetaCompleteRegistration(params, currentUser.id);
                    trackRedditSignUp(params, currentUser.id);
                }
                clearSignupAttempt();
            }
            if (referralEligibleUser === currentUser.id) trackPromoteKitSignup(currentUser);
        }).catch(error => console.warn('Signup attribution warning:', error.message));
        return () => { alive = false; };
    }, [currentUser, referralEligibleUser]);

    const markUserAsReauthenticated = () => {
        setHasReauthenticated(true);
    };

    const resetReauthenticationStatus = () => {
        setHasReauthenticated(false);
    };

    const setNextPageAfterReauth = (page) => {
        setNextPage(page);
    };

    const getNextPageAfterReauth = () => {
        return nextPage;
    };

    // Function to check premium status through Supabase RLS or custom claims
    const checkPremiumStatus = useCallback(async (force = false) => {
        if (!currentUser || currentUser.is_anonymous) { setPremiumState(null); return false; }
        const userId = currentUser.id;
        const generation = ++premiumRequestRef.current;
        const active = () => premiumUserRef.current === userId && premiumRequestRef.current === generation;
        setPremiumLoading(true);
        let timer;
        try {
            const { data, error } = await Promise.race([
                supabase.rpc('check_premium_status'),
                new Promise((_, reject) => { timer = setTimeout(() => reject(Object.assign(
                    new Error('Plan verification timed out.'), { code: 'request_timeout' })), 8000); }),
            ]);
            
            if (error) throw error;
            if (typeof data !== 'boolean') throw Object.assign(new Error('Plan response unavailable.'), { code: 'malformed_response' });
            if (!active()) return null;
            const premiumStatus = data;
            setPremiumState({ userId, premium: premiumStatus });
            setPremiumCheckedUser(userId);
            setPremiumError(false);
            setReferralEligibleUser(data === false ? currentUser.id : null);
            // Only a verified non-paying account with its original affiliate
            // may become a referral. Failed status checks must fail closed.
            if (data === false) trackPromoteKitSignup(currentUser);
            return premiumStatus;
        } catch (error) {
            if (!active()) return null;
            setReferralEligibleUser(null);
            setPremiumError(true);
            trackDiagnostic('premium_status_error', error, { operation: 'premium_lookup' });
            // Preserve this account's last verified display. The server still
            // verifies access; unknown plans never become a free-plan decision.
            return null;
        } finally {
            clearTimeout(timer);
            if (active()) setPremiumLoading(false);
        }
        }, [currentUser]); // include anything you read: currentUser is read above

    // Check premium status when user changes
    useEffect(() => {
        if (currentUser) {
            checkPremiumStatus();
        } else {
            setPremiumState(null);
            setPremiumError(false);
        }
        }, [currentUser, checkPremiumStatus]);

    useEffect(() => {
        setAnalyticsUser(currentUser, {
            account_plan: isPremium ? 'premium' : premiumCheckedUser === currentUser?.id || currentUser?.is_anonymous ? 'free' : 'unknown',
            auth_provider: currentUser?.app_metadata?.provider || 'unknown',
        });
    }, [currentUser, isPremium, premiumCheckedUser]);

    useEffect(() => {
        if (!currentUser?.id || currentUser.is_anonymous) {
            lastHealthUserId.current = null;
            return;
        }

        if (lastHealthUserId.current === currentUser.id) return;
        lastHealthUserId.current = currentUser.id;

        supabase.functions.invoke('health')
            .then(({ error }) => {
                if (error) console.warn('health invoke warning:', error.message || error);
            })
            .catch((error) => {
                console.warn('health invoke error:', error?.message || error);
            });
    }, [currentUser]);

    // Conditional rendering based on the loading state
    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            currentUser,
            guestConfig,
            accountReady: !currentUser || currentUser.is_anonymous || premiumCheckedUser === currentUser.id,
            ensureSession,
            isAuthInitialized,
            hasReauthenticated,
            markUserAsReauthenticated,
            resetReauthenticationStatus,
            setNextPageAfterReauth,
            getNextPageAfterReauth,
            checkPremiumStatus,
            isPremium,
            premiumLoading,
            premiumError,
        }}>
            {premiumError && currentUser && !currentUser.is_anonymous && <div className="guest-notice" role="alert">
                Your plan could not be verified right now. Your saved transcripts are still available.
                <button type="button" disabled={premiumLoading} onClick={() => checkPremiumStatus(true)}>
                    <span>{premiumLoading ? 'Checking plan…' : 'Retry plan verification'}</span>
                </button>
            </div>}
            {handoverError && <div className="guest-notice" role="alert">Your account is ready, but saving your guest history needs another try. {handoverError} <button onClick={saveGuestHistory}>Retry saving history</button> <a href={appUrl('/dashboard')}>Back to workspace</a></div>}
            {children}
            {challenge && <GuestVerification challenge={challenge} onClose={closeChallenge} />}
        </AuthContext.Provider>
    );
};
